import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Buildings, CheckCircle, GlobeHemisphereWest, Handshake,
  MagnifyingGlass, SealCheck, UsersThree,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ConstructionCompanyForm from "@/components/ConstructionCompanyForm";

const fieldClass =
  "h-11 border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const titleCase = (value) =>
  String(value || "—")
    .replaceAll("_", " ")
    .replace(/\\b\\w/g, (letter) => letter.toUpperCase());

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    gold: "border-[#C5A059]/40 bg-[#C5A059]/10 text-[#8A692D]",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <span className={`inline-flex items-center border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

function StatCard({ icon: Icon, value, label, gold = false }) {
  return (
    <div className="flex min-h-[112px] items-center gap-4 border border-slate-200 bg-white px-6 py-5">
      <div className={`flex h-11 w-11 items-center justify-center ${gold ? "text-[#B48736]" : "text-[#0B2545]"}`}>
        <Icon size={27} />
      </div>
      <div>
        <p className="font-serif text-2xl text-[#0B2545]">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function CompanyCard({ company, t }) {
  const primaryContact = (company.contacts || []).find(
    (contact) => contact.is_primary
  ) || (company.contacts || [])[0];

  const confidenceTone =
    company.confidence_level === "high"
      ? "green"
      : company.confidence_level === "medium"
        ? "amber"
        : "slate";

  return (
    <article className="flex h-full flex-col border border-slate-200 bg-white">
      <div className="h-1 bg-gradient-to-r from-[#0B2545] via-[#C5A059] to-[#0B2545]" />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-[#A77E35]">
              {titleCase((company.company_roles || [])[0])}
            </p>
            <h2 className="font-serif text-xl leading-tight text-[#0B2545]">
              {company.name}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {[company.headquarters_city, company.headquarters_country]
                .filter(Boolean)
                .join(", ") || "—"}
            </p>
          </div>
          <Badge tone="gold">{company.priority || "C"}</Badge>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-y border-slate-100 py-4">
          <Badge tone={confidenceTone}>
            {titleCase(company.confidence_level)}
          </Badge>
          <Badge tone={company.verification_status === "verified" ? "green" : "amber"}>
            {titleCase(company.verification_status)}
          </Badge>
          {company.operates_in_portugal && (
            <Badge tone="blue">
              {t("constructionPartners.portugalPresence", "Portugal presence")}
            </Badge>
          )}
        </div>

        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">
            {t("constructionPartners.specialities", "Specialities")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {(company.specialities || []).slice(0, 4).map((item) => (
              <span key={item} className="bg-slate-50 px-2 py-1 text-xs text-slate-600">
                {titleCase(item)}
              </span>
            ))}
          </div>
        </div>

        <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600">
          {company.why_relevant || company.description ||
            t("constructionPartners.noDescription", "No company description recorded.")}
        </p>

        <div className="mt-auto pt-5">
          <div className="border-t border-slate-100 pt-4 text-xs text-slate-500">
            {primaryContact?.email
              ? primaryContact.email
              : t("constructionPartners.contactMissing", "Professional contact requires verification")}
          </div>
          {company.official_url && (
            <a
              href={company.official_url}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex text-xs font-semibold uppercase tracking-wider text-[#0B2545] hover:text-[#A77E35]"
            >
              {t("constructionPartners.officialWebsite", "Official website")} →
            </a>
          )}
        </div>
      </div>
    </article>
  );
}


export default function ConstructionPartners() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [role, setRole] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [priority, setPriority] = useState("");
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();

  const params = useMemo(() => {
    const result = { limit: 500 };
    if (search.trim()) result.q = search.trim();
    if (country) result.country = country;
    if (role) result.role = role;
    if (speciality) result.speciality = speciality;
    if (priority) result.priority = priority;
    return result;
  }, [search, country, role, speciality, priority]);

  const { data: meta } = useQuery({
    queryKey: ["construction-companies-meta"],
    queryFn: async () =>
      (await api.get("/construction-companies/meta")).data,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["construction-companies", params],
    queryFn: async () =>
      (await api.get("/construction-companies", { params })).data,
  });

  const companies = useMemo(() => data?.results || [], [data]);

  const createMutation = useMutation({
    mutationFn: async (payload) =>
      (await api.post("/construction-companies", payload)).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["construction-companies"],
      });
      setShowForm(false);
      toast.success(
        t(
          "constructionPartners.form.created",
          "Construction company created successfully."
        )
      );
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.detail ||
          t(
            "constructionPartners.form.createError",
            "The construction company could not be created."
          )
      );
    },
  });

  const countries = useMemo(() => {
    const values = new Set();
    companies.forEach((company) => {
      if (company.headquarters_country) {
        values.add(company.headquarters_country);
      }
      (company.countries_of_operation || []).forEach((item) => {
        if (item) values.add(item);
      });
    });
    return Array.from(values).sort();
  }, [companies]);

  const statistics = useMemo(() => ({
    total: data?.total || 0,
    priority: companies.filter(
      (company) => company.priority === "A"
    ).length,
    verified: companies.filter(
      (company) => company.verification_status === "verified"
    ).length,
    portugal: companies.filter(
      (company) => company.operates_in_portugal
    ).length,
  }), [companies, data?.total]);

  return (
    <div className="space-y-8 pb-12">
      <header className="flex flex-col justify-between gap-6 xl:flex-row xl:items-end">
        <div>
          <p className="mb-3 text-xs uppercase tracking-[0.28em] text-[#A77E35]">
            {t(
              "constructionPartners.eyebrow",
              "Panatlantik delivery intelligence"
            )}
          </p>
          <h1 className="font-serif text-4xl leading-tight text-[#0B2545] md:text-5xl">
            {t(
              "constructionPartners.title",
              "Construction Partners"
            )}
          </h1>
          <p className="mt-3 max-w-3xl text-base leading-7 text-slate-500">
            {t(
              "constructionPartners.subtitle",
              "Verified contractors, developers, engineering groups and strategic partners matched to Panatlantik projects."
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/construction-partners/discover")}
            className="h-11 rounded-none border-[#C5A059] px-5"
          >
            <GlobeHemisphereWest size={18} className="mr-2" />
            {t(
              "constructionPartners.discover",
              "Discover companies"
            )}
          </Button>
          <Button
            type="button"
            onClick={() => setShowForm(true)}
            className="h-11 rounded-none bg-[#0B2545] px-5 hover:bg-[#12345e]"
          >
            <Buildings size={18} className="mr-2" />
            {t(
              "constructionPartners.addCompany",
              "Add company"
            )}
          </Button>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Buildings}
          value={statistics.total}
          label={t("constructionPartners.total", "Total companies")}
        />
        <StatCard
          icon={Handshake}
          value={statistics.priority}
          label={t("constructionPartners.priorityA", "Priority A partners")}
          gold
        />
        <StatCard
          icon={SealCheck}
          value={statistics.verified}
          label={t("constructionPartners.verified", "Verified companies")}
        />
        <StatCard
          icon={UsersThree}
          value={statistics.portugal}
          label={t("constructionPartners.portugal", "Operating in Portugal")}
          gold
        />
      </section>

      <section className="border border-slate-200 bg-white p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(280px,1fr)_repeat(4,minmax(150px,0.35fr))]">
          <label className="relative">
            <MagnifyingGlass
              size={19}
              className="absolute left-4 top-3 text-slate-400"
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t(
                "constructionPartners.search",
                "Search company, project, country or speciality…"
              )}
              className={`${fieldClass} w-full pl-11`}
            />
          </label>

          <select
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("constructionPartners.allCountries", "All countries")}
            </option>
            {countries.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>

          <select
            value={role}
            onChange={(event) => setRole(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("constructionPartners.allRoles", "All company roles")}
            </option>
            {(meta?.company_roles || []).map((item) => (
              <option key={item} value={item}>{titleCase(item)}</option>
            ))}
          </select>

          <select
            value={speciality}
            onChange={(event) => setSpeciality(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("constructionPartners.allSpecialities", "All specialities")}
            </option>
            {(meta?.specialities || []).map((item) => (
              <option key={item} value={item}>{titleCase(item)}</option>
            ))}
          </select>

          <select
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("constructionPartners.allPriorities", "All priorities")}
            </option>
            {(meta?.priorities || []).map((item) => (
              <option key={item} value={item}>
                {t("constructionPartners.priority", "Priority")} {item}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="border-l-2 border-[#C5A059] bg-slate-100 px-5 py-4 text-sm text-[#0B2545]">
        {t(
          "constructionPartners.notice",
          "Internal partner intelligence. Verify company and contact information before external communication."
        )}
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-slate-500">
          {t("constructionPartners.loading", "Loading construction partners…")}
        </div>
      ) : isError ? (
        <div className="border border-red-200 bg-red-50 p-6 text-red-700">
          {t(
            "constructionPartners.loadError",
            "Construction partners could not be loaded."
          )}
        </div>
      ) : companies.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white py-20 text-center">
          <CheckCircle size={36} className="mx-auto text-[#C5A059]" />
          <h2 className="mt-4 font-serif text-2xl text-[#0B2545]">
            {t(
              "constructionPartners.noResults",
              "No construction companies found"
            )}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {t(
              "constructionPartners.noResultsHelp",
              "Import the researched companies or adjust the search filters."
            )}
          </p>
        </div>
      ) : (
        <section className="grid items-stretch gap-5 lg:grid-cols-2 xl:grid-cols-3">
          {companies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              t={t}
            />
          ))}
        </section>
      )}
      {showForm && (
        <ConstructionCompanyForm
          meta={meta}
          saving={createMutation.isPending}
          onCancel={() => setShowForm(false)}
          onSave={(payload) => createMutation.mutate(payload)}
          t={t}
        />
      )}
    </div>
  );
}
